/**
 * Generic, code-defined announcement system.
 *
 * An announcement is a post-login nudge shown per campaign `id`. The primary
 * CTA or the X dismisses it; "Not now" snoozes it for `snoozeMs`. Bump an
 * entry's `id` to re-show it after editing its content. State is per-browser
 * (localStorage) — there is no backend.
 *
 * By default an announcement shows once and stays dismissed. Set `repeat: true`
 * with `frequencyDays` to turn it into a recurring reminder that comes back on
 * a cadence (e.g. a weekly "complete your profile" nudge).
 *
 * To announce something new, add an entry to the registry in
 * `src/components/announcements/announcementRegistry.ts`.
 */
import type { LucideIcon } from "lucide-react";
import type { UserRole } from "./roles";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Viewer context passed to an announcement's `isActive` gate. */
export type AnnouncementContext = {
  nowMs: number;
  /** The signed-in user's role — gate on the same RBAC a route/section uses. */
  role: UserRole;
};

export type AnnouncementAction = {
  /** Text on the primary button. */
  label: string;
  /** Internal route pushed via react-router. */
  to?: string;
  /** External URL opened in a new tab. */
  href?: string;
  /**
   * Custom handler. When present it runs instead of `to`/`href` navigation —
   * use it for actions that aren't a plain link (open a modal, dispatch, etc.).
   */
  onClick?: () => void;
};

export type Announcement = {
  /** Stable campaign id; bump to re-show after the content changes. */
  id: string;
  icon: LucideIcon;
  /** Small pill above the title, e.g. "New". Omit to hide. */
  badge?: string;
  title: string;
  body: string;
  primary: AnnouncementAction;
  /** When set, a "Not now" button appears and hides it for this long. */
  snoozeMs?: number;
  /**
   * When true, the announcement returns every `frequencyDays` after each
   * dismissal instead of staying gone. Defaults to a one-time announcement.
   */
  repeat?: boolean;
  /**
   * Cadence in days for a repeating announcement (default 1). Ignored unless
   * `repeat` is true. Example: `repeat: true, frequencyDays: 7` → weekly.
   */
  frequencyDays?: number;
  /**
   * Extra eligibility gate beyond seen/snooze — return false to suppress.
   * Receives the viewer context, so it can gate on time ("still new"), on role
   * (only users who can access the target section), or both. Defaults to
   * always eligible.
   */
  isActive?: (ctx: AnnouncementContext) => boolean;
};

/** Per-id interaction state. `seenAt` is the last dismissal (unix ms). */
type AnnouncementState = {
  seenAt?: number;
  snoozeUntil?: number;
};

/** JSON `Record<id, AnnouncementState>`. */
const STORE_KEY = "ui.announcements";

function readStore(): Record<string, AnnouncementState> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, AnnouncementState>;
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, AnnouncementState>): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

/** Whether the announcement has ever been dismissed. */
export function isAnnouncementSeen(id: string): boolean {
  return readStore()[id]?.seenAt != null;
}

/**
 * Record a dismissal (primary CTA or X). For one-time announcements this hides
 * it for good; for repeating ones it starts the next `frequencyDays` interval.
 */
export function markAnnouncementSeen(
  id: string,
  nowMs: number = Date.now(),
): void {
  const store = readStore();
  // Replace the record so any pending snooze is cleared.
  store[id] = { seenAt: nowMs };
  writeStore(store);
}

/** Snooze — used by "Not now"; the announcement can return after `ms`. */
export function snoozeAnnouncement(
  id: string,
  ms: number,
  nowMs: number = Date.now(),
): void {
  const store = readStore();
  store[id] = { ...store[id], snoozeUntil: nowMs + ms };
  writeStore(store);
}

export function shouldShowAnnouncement(
  announcement: Announcement,
  ctx: AnnouncementContext,
): boolean {
  const { nowMs } = ctx;
  if (announcement.isActive && !announcement.isActive(ctx)) return false;

  const state = readStore()[announcement.id];
  if (state?.snoozeUntil != null && state.snoozeUntil > nowMs) return false;

  // Never dismissed → always eligible.
  if (state?.seenAt == null) return true;

  // Dismissed before: one-time stays gone; repeating returns after the cadence.
  if (!announcement.repeat) return false;
  const intervalMs = (announcement.frequencyDays ?? 1) * DAY_MS;
  return nowMs - state.seenAt >= intervalMs;
}

/** All currently-eligible announcements, in registry (priority) order. */
export function getActiveAnnouncements(
  registry: readonly Announcement[],
  ctx: AnnouncementContext,
): Announcement[] {
  return registry.filter((a) => shouldShowAnnouncement(a, ctx));
}
